import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { H2, Icons, PrimaryButton, SecondaryButton } from '../../../../../components';
import {
  cancelListingSubscriptionAsync,
  currentSubscriptionIdSelector,
  deleteStripeSubscriptionPrice,
  subscriptionPlanSelector,
} from '../../../../../ducks/stripe.duck';
import { FormattedMessage } from 'react-intl';
import css from './RenderUserSubscriptionMaybe.module.css';
import IconSpinner from '../../../../../components/IconSpinner/IconSpinner';
import classNames from 'classnames';
import { userSelector } from '../../../../../ducks/user.duck';
import {
  getUserSubscriptionDetails,
  getUserSubscriptionPlanId,
  getUserSubscriptionStatus,
  getUserType,
  isUserTalent,
} from '../../../../../util/userDataExtraction';
import {
  formatUnixDate,
  isArrayLength,
  normalizedStatus,
} from '../../../../../util/genericHelpers';
import SubscriptionPaymentModal from '../../../../SubscriptionPage/SubscriptionPaymentModal';
import { filterSubscriptionPricesByType } from '../../../../../util/dataExtractor';
import { SubscriptionStatus } from '../../../../../util/enums';
import {
  canUserCancelSubscription,
  getProductPriceDetails,
  isCancellationDateInFuture,
} from '../../../../../util/subscriptionHelpers';
import CustomFeatureList from './CustomFeatureList';
import { selectIsAuthenticated } from '../../../../../ducks/auth.duck';
import { useRouteConfiguration } from '../../../../../context/routeConfigurationContext';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { createResourceLocatorString } from '../../../../../util/routes';

const RenderUserSubscriptionMaybe = props => {
  const {
    subscriptions,
    lookupKeys,
    isListingPage,
    sectionRef,
    onEdit,
    isOwnListing,
    authorId,
    listingId,
    isEditListingPage,
    currentUserSubscription,
  } = props;
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const currentSubscriptionId = useSelector(currentSubscriptionIdSelector);
  const routeConfiguration = useRouteConfiguration();
  const history = useHistory();
  const currentUser = useSelector(userSelector);
  const [subscriptionList, setSubscriptionList] = useState(subscriptions);
  const [paymentMethodModalState, setPaymentMethodModalState] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const activeSubscriptionPlanId = getUserSubscriptionPlanId(currentUser);
  const userSubscriptionDetails = getUserSubscriptionDetails(currentUser);
  const [shouldUpdate, setShouldUpdate] = useState(false);
  const { subscriptionPrices, retrieveSubscriptionPriceInProgress } = useSelector(
    subscriptionPlanSelector
  );

  const subscriptionStatus = normalizedStatus(getUserSubscriptionStatus(currentUser));
  const activeStatus = normalizedStatus(SubscriptionStatus.ACTIVE);
  const isSubscriptionActive = subscriptionStatus === activeStatus;

  const { id: subscriptionId } = userSubscriptionDetails || {};
  const userType = getUserType(currentUser);
  const talentPrices = filterSubscriptionPricesByType(subscriptionPrices, userType);
  const [inProgress, setInProgress] = useState(false);
  const dispatch = useDispatch();
  if (!isArrayLength(subscriptionList)) {
    return null;
  }

  const isTalent = isUserTalent(currentUser);

  const handleDelete = async item => {
    const priceId = item.id;
    setInProgress(priceId); // Set the in-progress state to the current priceId
    const lookupKey = item.lookup_key;
    const updatedLookupKeys = lookupKeys.filter(key => key !== lookupKey) || [];
    try {
      // Dispatch the Redux thunk to delete the subscription price
      await dispatch(
        deleteStripeSubscriptionPrice({
          priceId: priceId,
          lookupKeys: updatedLookupKeys,
        })
      );
      // Update the subscription list by removing the deleted subscription
      setSubscriptionList(prevSubscriptions =>
        prevSubscriptions.filter(subscription => subscription.id !== priceId)
      );
    } catch (error) {
    } finally {
      setInProgress(false); // Reset in-progress state
    }
  };

  const parseCustomFeatures = featuresString => {
    try {
      return JSON.parse(featuresString);
    } catch (error) {
      return null; // Return null if parsing fails
    }
  };

  // Format feature key to human-readable form (e.g., camelCase -> Camel Case)
  const formatFeatureName = key =>
    key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();

  // Generate feature details based on value type
  const getFeatureDetails = (key, value, formattedKey) => {
    if (typeof value === 'object') {
      if (value.frequency) {
        return `${value.frequency} ${formattedKey} Per Billing Cycle`;
      }
      return `${formattedKey} ${value.enabled ? 'Included' : 'Not Included'}`;
    }
    return `${formattedKey} ${value ? 'Included' : 'Not Included'}`;
  };

  const renderCustomFeatures = customFeatures => {
    const featureList = Object.entries(customFeatures).map(([key, value]) => {
      const formattedKey = formatFeatureName(key);
      return {
        key,
        name: formattedKey,
        details: getFeatureDetails(key, value, formattedKey),
      };
    });

    return (
      <ul className="featuresList">
        {featureList.map(({ key, name, details }) => (
          <li key={key} className="featureItem">
            <Icons name="checkListIcon" />
            <span>
              <strong>{name}</strong>: {details}
            </span>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div
      className={classNames(
        css.subscriptionContainer,
        isListingPage ? css.listingCardContainer : null
      )}
      ref={sectionRef}
    >
      {subscriptionList.map((item, index) => {
        const customFeatures =
          item?.metadata?.customFeatures && parseCustomFeatures(item.metadata.customFeatures);
        const tasks = item?.metadata?.tasks && parseCustomFeatures(item.metadata.tasks);
        const { priceAmount, priceName, priceId, productMetadata, isFree } =
          getProductPriceDetails(item, isSubscriptionActive) || {};
        const isActive = activeSubscriptionPlanId === priceId || isFree;
        const downgradeToFreePlan = priceAmount == 0 && !isActive;
        // Find matching subscription for this price
        const matchingSubscription =
          currentUserSubscription?.find(sub => sub.priceId === priceId) || null;
        // Per-subscription variables
        const userPriceId = matchingSubscription?.priceId;
        const userSubscriptionId = matchingSubscription?.subscriptionId;
        const cancellationDateUnix = matchingSubscription?.cancelAt;
        const willCancelAtPeriodEnd = matchingSubscription?.cancelAtPeriodEnd;
        // Format the cancellation date
        const formattedCancellationDate =
          cancellationDateUnix && formatUnixDate(cancellationDateUnix);
        const isCancellationDateValid = isCancellationDateInFuture(cancellationDateUnix);
        const isCurrentUserSubscriptionActive =
          matchingSubscription?.status === SubscriptionStatus.ACTIVE;

        const shouldShowSubscribeButton =
          (isListingPage && !isCurrentUserSubscriptionActive && !isOwnListing) ||
          (!isCancellationDateValid && willCancelAtPeriodEnd);
        // ============================================
        // Create display message (only if date is in future)
        // ============================================
        const subscriptionStatusMessage =
          willCancelAtPeriodEnd &&
          userPriceId === priceId &&
          formattedCancellationDate &&
          isCancellationDateValid
            ? `Subscription active till ${formattedCancellationDate}`
            : null;
        const showCancelSubscriptionButton = canUserCancelSubscription({
          isAuthenticated,
          isListingPage,
          isSubscriptionActive: isCurrentUserSubscriptionActive,
          isOwnListing,
          userPrice: userPriceId,
          planPrice: priceId,
          isPendingCancellation: willCancelAtPeriodEnd,
          cancellationDate: formattedCancellationDate,
        });
        const showSubscribedMessage =
          isListingPage &&
          isCurrentUserSubscriptionActive &&
          !isOwnListing &&
          !willCancelAtPeriodEnd;
        const handleSubscriptionClick = () => {
          if (isAuthenticated) {
            setPaymentMethodModalState({
              priceId,
              priceName: productMetadata?.type,
              subscriptionType: priceName,
              downgradeToFreePlan,
            });
          } else {
            history.push(createResourceLocatorString('SignupPage', routeConfiguration, {}, {}));
          }
        };

        const handleCancelListingSubscription = () => {
          try {
            const cancelParams = {
              subscriptionId: userSubscriptionId,
              listingId,
              authorId,
            };
            dispatch(cancelListingSubscriptionAsync(cancelParams));
          } catch (error) {}
        };
        const cancelSubscriptionInProgress = currentSubscriptionId === userSubscriptionId;
        return (
          <div className={css.cardContainer} key={item?.id}>
            <div className={css.cardHead} key={index}>
              <div className={css.headingContainer}>
                <div className={css.priceName}>{item.product.name}</div>
              </div>
              <H2 as="h2" className={css.heading}>
                ${(item.unit_amount / 100).toFixed(2)}
                <sub>
                  {item.recurring.interval === 'month' ? (
                    <FormattedMessage
                      id="RenderUserSubscriptionMaybe.perMonth"
                      defaultMessage="Per month"
                    />
                  ) : (
                    <FormattedMessage
                      id="RenderUserSubscriptionMaybe.perYear"
                      defaultMessage="Per year"
                    />
                  )}
                </sub>
              </H2>
              <div className={css.buttonContainer}>
                {/* {isOwnListing && (
                  <PrimaryButton
                    type="button"
                    className={css.getStartedButton}
                    onClick={() => onEdit(item)}
                  >
                    <FormattedMessage id="RenderUserSubscriptionMaybe.editSubscription" />
                  </PrimaryButton>
                )} */}
                {shouldShowSubscribeButton && (
                  <PrimaryButton
                    type="button"
                    className={css.getStartedButton}
                    onClick={handleSubscriptionClick}
                  >
                    <FormattedMessage id="RenderUserSubscriptionMaybe.purchase" />
                  </PrimaryButton>
                )}

                {subscriptionStatusMessage && (
                  <p className={css.subscriptionStatusText}>{subscriptionStatusMessage}</p>
                )}
                {showCancelSubscriptionButton && (
                  <PrimaryButton
                    type="button"
                    className={css.getStartedButton}
                    onClick={handleCancelListingSubscription}
                    inProgress={cancelSubscriptionInProgress}
                    disabled={cancelSubscriptionInProgress}
                  >
                    <FormattedMessage id="RenderUserSubscriptionMaybe.cancelSubscription" />
                  </PrimaryButton>
                )}
                {showSubscribedMessage && (
                  <div className={css.subscribedText}>
                    <FormattedMessage id="RenderUserSubscriptionMaybe.subscribed" />
                  </div>
                )}
                {!isListingPage &&
                  (inProgress === item.id ? (
                    <IconSpinner className={css.spinner} />
                  ) : (
                    <SecondaryButton
                      role="button"
                      className={css.deleteButton}
                      onClick={() => handleDelete(item)}
                    >
                      <FormattedMessage id="RenderUserSubscriptionMaybe.removePlan" />
                    </SecondaryButton>
                  ))}
              </div>
            </div>
            {customFeatures && Object.keys(customFeatures).length > 0 && (
              <div className={css.planDetails}>
                <div className={css.planHead}>
                  <h2>
                    <FormattedMessage
                      id="RenderUserSubscriptionMaybe.servicesIncluded"
                      defaultMessage="Service Included:"
                    />
                  </h2>
                </div>
                <CustomFeatureList features={customFeatures} />
              </div>
            )}
            {isArrayLength(tasks) && (
              <div className={css.servicesSection}>
                <h2 className={css.servicesHeading}>
                  <FormattedMessage
                    id="RenderUserSubscriptionMaybe.servicesIncluded"
                    defaultMessage="Service Included:"
                  />
                </h2>
                <div className={css.servicesList}>
                  {tasks.map((task, index) => (
                    <div className={css.serviceItem} key={`task-${index}`}>
                      <div className={css.serviceItemContent}>
                        <span className={css.serviceIcon}>
                          <Icons name="checkListIcon" />
                        </span>
                        <span className={css.serviceText}>{task}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}

      <SubscriptionPaymentModal
        isOpen={!!paymentMethodModalState?.priceId}
        setPaymentMethodModalState={setPaymentMethodModalState}
        selectedPlanId={paymentMethodModalState?.priceId}
        selectedPlanName={paymentMethodModalState?.priceName}
        subscriptionType={paymentMethodModalState?.subscriptionType}
        currentUser={currentUser}
        activeSubscriptionPlanId={activeSubscriptionPlanId}
        shouldUpdate={shouldUpdate}
        subscriptionId={subscriptionId}
        userType={userType}
        setIsConfirmationModalOpen={() => {
          setIsConfirmationModalOpen(true);
        }}
        authorId={authorId}
        listingId={listingId}
        isListingPage={isListingPage}
      />
    </div>
  );
};

export default RenderUserSubscriptionMaybe;
